import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, DataTable, Td, FormDialog, Field, TextInput, ActionButton, EmptyState } from "../components/common";

const EMPTY = { employee_number: "", full_name: "", first_name: "", middle_name: "", last_name: "", department: "", location: "" };

export default function Drivers() {
  const { user } = useAuth();
  const isAdmin = ["admin", "shop_manager"].includes(user?.role);
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/drivers").then((r) => setDrivers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      employee_number: d.employee_number || "", full_name: d.full_name || "", first_name: d.first_name || "",
      middle_name: d.middle_name || "", last_name: d.last_name || "", department: d.department || "", location: d.location || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { full_name: form.full_name };
    for (const k of ["employee_number", "first_name", "middle_name", "last_name", "department", "location"]) {
      if (form[k]) payload[k] = form[k];
    }
    try {
      if (editing) {
        await api.patch(`/drivers/${editing.id}`, payload);
        toast.success("Driver updated");
      } else {
        await api.post("/drivers", payload);
        toast.success("Driver added");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (d) => {
    try {
      await api.delete(`/drivers/${d.id}`);
      toast.success(`${d.full_name} deleted`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="drivers-page">
      <PageHeader title="Drivers" subtitle="Company driver roster shared across all modules" testId="drivers-header">
        {isAdmin && (
          <ActionButton testId="driver-add-button" onClick={openAdd}>
            <Plus size={16} /> Add Driver
          </ActionButton>
        )}
      </PageHeader>

      {drivers.length === 0 ? (
        <EmptyState message="No drivers" testId="drivers-empty" />
      ) : (
        <DataTable
          testId="drivers-table"
          columns={["Employee #", "Full Name", "First Name", "Middle Name", "Last Name", "Department", "Location", ...(isAdmin ? [""] : [])]}
          rows={drivers}
          renderRow={(d) => (
            <tr key={d.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`driver-row-${d.id}`}>
              <Td className="font-mono text-amber-500">{d.employee_number || "—"}</Td>
              <Td className="font-medium text-slate-100">{d.full_name}</Td>
              <Td className="text-slate-400">{d.first_name || "—"}</Td>
              <Td className="text-slate-400">{d.middle_name || "—"}</Td>
              <Td className="text-slate-400">{d.last_name || "—"}</Td>
              <Td className="text-slate-400">{d.department || "—"}</Td>
              <Td className="text-slate-400">{d.location || "—"}</Td>
              {isAdmin && (
                <Td>
                  <div className="flex gap-1">
                    <button data-testid={`driver-edit-${d.id}`} onClick={() => openEdit(d)} className="p-1.5 text-slate-500 hover:text-amber-400 transition-colors"><Pencil size={14} /></button>
                    <button data-testid={`driver-delete-${d.id}`} onClick={() => remove(d)} className="p-1.5 text-slate-500 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </Td>
              )}
            </tr>
          )}
        />
      )}

      <FormDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }} title={editing ? `Edit ${editing.full_name}` : "Add Driver"} testId="driver-dialog">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Employee number">
              <TextInput testId="driver-emp-number-input" value={form.employee_number} onChange={(e) => setForm({ ...form, employee_number: e.target.value })} />
            </Field>
            <Field label="Full name">
              <TextInput testId="driver-full-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required placeholder='e.g. "Acklin, Jase G."' />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="First name">
              <TextInput testId="driver-first-name-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </Field>
            <Field label="Middle name">
              <TextInput testId="driver-middle-name-input" value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} />
            </Field>
            <Field label="Last name">
              <TextInput testId="driver-last-name-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </Field>
          </div>
          <Field label="Department">
            <TextInput testId="driver-department-input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="e.g. Trucking : Vacuum Trucks" />
          </Field>
          <Field label="Location">
            <TextInput testId="driver-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Corporate Office : Northwest Louisiana : Frierson" />
          </Field>
          <ActionButton testId="driver-submit-button" type="submit" disabled={busy} className="w-full justify-center">
            {busy ? "Saving…" : editing ? "Save Changes" : "Add Driver"}
          </ActionButton>
        </form>
      </FormDialog>
    </div>
  );
}
